import { memo, useCallback, useEffect, useState } from 'react'
import { Box, IconButton, Typography, useTheme } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Chart from '../chart'
import { ChartEvents } from '../chart.events'
import type { TouchDrawStep } from '../drawingTools/drawing.touchDraw'

type Props = {
    chart: Chart
}

/**
 * Step banner for touch cursor drawing ("1 of 4 · Move cursor to start point" with a trash button
 * to cancel). Driven by `ChartEvents.DRAWING_TOUCH_STEP`, published by `DrawingBase` while a drawing
 * is being placed on a touch-enabled chart. Renders nothing when no touch drawing is in progress.
 */
const TouchDrawStepBanner: React.FC<Props> = ({ chart }) => {
    const theme = useTheme()
    const [step, setStep] = useState<TouchDrawStep | null>(null)

    useEffect(() => {
        const onStep = (event: CustomEvent<TouchDrawStep | null>) => {
            setStep(event.detail ?? null)
        }
        const onInactive = () => setStep(null)
        chart.subscribeEvent(ChartEvents.DRAWING_TOUCH_STEP, onStep)
        chart.subscribeEvent(ChartEvents.DRAWING_INACTIVE, onInactive)
        return () => {
            chart.unsubscribeEvent(ChartEvents.DRAWING_TOUCH_STEP, onStep)
            chart.unsubscribeEvent(ChartEvents.DRAWING_INACTIVE, onInactive)
            setStep(null)
        }
    }, [chart])

    const onCancel = useCallback(() => {
        chart.drawingManager?.abort()
        setStep(null)
    }, [chart])

    if (!step) return null

    const dark = theme.palette.mode === 'dark'
    const bannerBg = dark ? 'rgba(40, 40, 44, 0.96)' : 'rgba(28, 28, 30, 0.92)'
    const chipBg = 'rgba(255, 255, 255, 0.16)'
    const fg = '#ffffff'

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                zIndex: 30,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 3,
                backgroundColor: bannerBg,
                color: fg,
                boxShadow: theme.shadows[4],
                pointerEvents: 'auto',
                userSelect: 'none'
            }}
        >
            {step.totalSteps !== undefined ? (
                <Typography
                    component="span"
                    sx={{
                        flexShrink: 0,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1.5,
                        backgroundColor: chipBg,
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap'
                    }}
                >
                    {step.step} of {step.totalSteps}
                </Typography>
            ) : (
                <Typography
                    component="span"
                    sx={{
                        flexShrink: 0,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1.5,
                        backgroundColor: chipBg,
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap'
                    }}
                >
                    Point {Math.ceil(step.step / 2)}
                </Typography>
            )}
            <Typography
                component="span"
                sx={{
                    flex: 1,
                    textAlign: 'center',
                    fontSize: 15,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {step.message}
            </Typography>
            <IconButton
                aria-label="Cancel drawing"
                size="small"
                onClick={onCancel}
                sx={{ color: fg, flexShrink: 0 }}
            >
                <DeleteOutlineIcon fontSize="small" />
            </IconButton>
        </Box>
    )
}

export default memo(TouchDrawStepBanner)
